import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PlusIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { TableConfig, MultiYearForecastData } from '@/types/rebateAnalytics';
import RebateCustomBreakdownTableCard from './RebateCustomBreakdownTableCard';
import { CreateAnalyticsTableModal } from './CreateAnalyticsTableModal';

interface TableConfigManagerProps {
  tableConfigs: TableConfig[];
  onAddTable: (
    config: Omit<TableConfig, 'id' | 'label'>,
    label: string,
  ) => void;
  onUpdateTable: (
    id: string,
    config: Omit<TableConfig, 'id' | 'label'>,
  ) => void;
  onUpdateTableLabel: (id: string, label: string) => void;
  onDeleteTable: (id: string) => void;
  onReorderTables: (newOrder: TableConfig[]) => void;
  // Data props
  multiYearForecastData: MultiYearForecastData;
  // isCumulative indicates whether data is already cumulative (from parent transformation)
  isCumulative: boolean;
  supplierIdToName: Map<string, string>;
  divisionIdToName: Map<string, string>;
  // Tag maps passed from parent to avoid duplicate API calls
  ruleTagsMap: Record<string, string[]>;
  agreementTagsMap: Record<string, string[]>;
  supplierTagsMap: Record<string, string[]>;
  ruleTagIdToName: Map<string, string>;
  agreementTagIdToName: Map<string, string>;
  supplierTagIdToName: Map<string, string>;
  ruleIdToName: Map<string, string>;
  agreementIdToName: Map<string, string>;
  creatorName?: string;
  activeFilterCount?: number;
  activeFilterNames?: string[];
  primaryLine?: string; // Primary line selection (determines which data source tables use)
}

export function RebateAnalyticsTables({
  tableConfigs,
  onAddTable,
  onUpdateTable,
  onUpdateTableLabel,
  onDeleteTable,
  onReorderTables,
  multiYearForecastData,
  isCumulative,
  supplierIdToName,
  divisionIdToName,
  ruleTagsMap,
  agreementTagsMap,
  supplierTagsMap,
  ruleTagIdToName,
  agreementTagIdToName,
  supplierTagIdToName,
  ruleIdToName,
  agreementIdToName,
  creatorName,
  activeFilterCount,
  activeFilterNames,
  primaryLine = 'sarima', // Default to SARIMA prediction algorithm
}: TableConfigManagerProps) {
  // State for create table modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Collapse state for each table
  const [collapsedTables, setCollapsedTables] = useState<Set<string>>(
    new Set(),
  );

  const toggleTableCollapse = useCallback((tableId: string) => {
    setCollapsedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableId)) {
        next.delete(tableId);
      } else {
        next.add(tableId);
      }
      return next;
    });
  }, []);

  // Track previous tableConfigs to detect changes
  const [prevTableConfigs, setPrevTableConfigs] = useState(tableConfigs);

  // Ordered tables state for drag-and-drop
  // Backend returns tables ordered by displayOrder DESC (highest first)
  const [orderedTables, setOrderedTables] = useState<TableConfig[]>(() => [
    ...tableConfigs,
  ]);

  // Adjust ordered tables during rendering when tableConfigs changes externally
  // (preserve local reordering from drag-and-drop)
  if (tableConfigs !== prevTableConfigs) {
    // Check if the change is just reordering (same IDs) or actual content change
    const currentIds = new Set(orderedTables.map((t) => t.id));
    const newIds = new Set(tableConfigs.map((t) => t.id));

    // Detect newly added tables (exist in newIds but not in currentIds)
    const newlyAddedIds = Array.from(newIds).filter(
      (id) => !currentIds.has(id),
    );

    // If IDs match, check if order actually changed or just content updated
    if (
      currentIds.size === newIds.size &&
      Array.from(currentIds).every((id) => newIds.has(id))
    ) {
      // Same tables - check if order changed
      const currentOrder = orderedTables.map((t) => t.id);
      const newOrder = tableConfigs.map((t) => t.id);
      const orderChanged =
        currentOrder.length !== newOrder.length ||
        currentOrder.some((id, index) => id !== newOrder[index]);

      if (orderChanged) {
        // Order changed externally - sync from props
        setOrderedTables([...tableConfigs]);
      } else {
        // Order unchanged, just content updated - preserve order and update table data
        const tableConfigMap = new Map(tableConfigs.map((t) => [t.id, t]));
        setOrderedTables(
          orderedTables.map((t) => tableConfigMap.get(t.id) ?? t),
        );
      }
    } else if (newlyAddedIds.length > 0) {
      // New table(s) added - ensure they appear at the top
      // Get new tables from props (they might be in any position)
      const newTables = tableConfigs.filter((t) =>
        newlyAddedIds.includes(t.id),
      );
      // Preserve existing tables in their current order (respects user drag-and-drop)
      const existingTablesInOrder = orderedTables.filter((t) =>
        newIds.has(t.id),
      );
      // Put new tables at the top (newest first, matching backend DESC order)
      // then existing tables in their current order
      setOrderedTables([...newTables.reverse(), ...existingTablesInOrder]);
    } else {
      // Table(s) removed - reset from props
      setOrderedTables([...tableConfigs]);
    }
    setPrevTableConfigs(tableConfigs);
  }

  // Handle moving table up in the list
  const handleMoveUp = useCallback(
    (tableId: string) => {
      const index = orderedTables.findIndex((table) => table.id === tableId);
      if (index > 0) {
        const newOrderedTables = [...orderedTables];
        [newOrderedTables[index - 1], newOrderedTables[index]] = [
          newOrderedTables[index],
          newOrderedTables[index - 1],
        ];
        setOrderedTables(newOrderedTables);
        // Backend expects order with highest displayOrder first (DESC ordering)
        onReorderTables(newOrderedTables);
      }
    },
    [orderedTables, onReorderTables],
  );

  // Handle moving table down in the list
  const handleMoveDown = useCallback(
    (tableId: string) => {
      const index = orderedTables.findIndex((table) => table.id === tableId);
      if (index < orderedTables.length - 1) {
        const newOrderedTables = [...orderedTables];
        [newOrderedTables[index], newOrderedTables[index + 1]] = [
          newOrderedTables[index + 1],
          newOrderedTables[index],
        ];
        setOrderedTables(newOrderedTables);
        // Backend expects order with highest displayOrder first (DESC ordering)
        onReorderTables(newOrderedTables);
      }
    },
    [orderedTables, onReorderTables],
  );

  return (
    <div className="space-y-4 w-full min-w-0 max-w-full overflow-hidden">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Analytics Tables</h2>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            variant="outline"
            className="rounded-md h-9 px-5 hover:bg-accent"
          >
            <PlusIcon className="h-4 w-4" />
            Create New Table
          </Button>
        </div>
      </div>

      <CreateAnalyticsTableModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={onAddTable}
        creatorName={creatorName}
        multiYearForecastData={multiYearForecastData}
        supplierIdToName={supplierIdToName}
        divisionIdToName={divisionIdToName}
        isCumulative={isCumulative}
        ruleTagIdToName={ruleTagIdToName}
        agreementTagIdToName={agreementTagIdToName}
        supplierTagIdToName={supplierTagIdToName}
        ruleTagsMap={ruleTagsMap}
        agreementTagsMap={agreementTagsMap}
        supplierTagsMap={supplierTagsMap}
        ruleIdToName={ruleIdToName}
        agreementIdToName={agreementIdToName}
      />

      {tableConfigs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No custom tables created yet. Use the form above to create your
              first table.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 w-full max-w-full min-w-0">
          {orderedTables.map((config, index) => (
            <RebateCustomBreakdownTableCard
              key={config.id}
              config={config}
              onUpdate={(updatedConfig) =>
                onUpdateTable(config.id, updatedConfig)
              }
              onUpdateLabel={onUpdateTableLabel}
              onDelete={() => onDeleteTable(config.id)}
              multiYearForecastData={multiYearForecastData}
              isCumulative={isCumulative}
              primaryLine={primaryLine}
              supplierIdToName={supplierIdToName}
              divisionIdToName={divisionIdToName}
              isCollapsed={collapsedTables.has(config.id)}
              onToggleCollapse={() => toggleTableCollapse(config.id)}
              ruleTagIdToName={ruleTagIdToName}
              agreementTagIdToName={agreementTagIdToName}
              supplierTagIdToName={supplierTagIdToName}
              ruleTagsMap={ruleTagsMap}
              agreementTagsMap={agreementTagsMap}
              supplierTagsMap={supplierTagsMap}
              onMoveUp={() => handleMoveUp(config.id)}
              onMoveDown={() => handleMoveDown(config.id)}
              isFirst={index === 0}
              isLast={index === orderedTables.length - 1}
              activeFilterCount={activeFilterCount}
              activeFilterNames={activeFilterNames}
            />
          ))}
        </div>
      )}
    </div>
  );
}
